import {
  ArrowRed,
  Box,
  Disclaimer,
  displayCentsInDollars,
  Div,
  H3,
  H5,
  Icon,
  lightTheme,
  P,
  TOOLTIP_ID,
} from '@vgrubs/components';
import { ReactNode } from 'react';
import { BarSegmentSpec, ValueBar } from './ValueBar';
import {
  CardRow,
  CardTag,
  Headline,
  IconTile,
  InlineStats,
  StatDot,
  StatLabel,
  TitleLine,
} from './ValueCard.styled';

export type ValueStatSpec = {
  label: string;
  /** Cents. */
  amount: number;
  /** Its tooltip: what it stands on (orders, deliveries, the biggest line). */
  hint: string;
  /** The colour of its bar segment, when it has one. */
  dot?: string;
};

type Props = {
  title: string;
  value: string;
  valueColor: string;
  icon: ReactNode;
  /** Behind a white glyph; left out for a logo that is its own circle. */
  iconBackground?: string;
  tag?: { label: string; color: string; background: string } | null;
  stats: ValueStatSpec[];
  bar: { segments: BarSegmentSpec[]; scale: number };
  onOpen: () => void;
};

/**
 * One of the Overview's value cards: an icon, the headline and its figures
 * in a row, a bar of what the headline is made of across the bottom, and the
 * arrow in the same corner as the chart cards under it, opening the whole
 * breakdown.
 */
export const ValueCard = ({
  title,
  value,
  valueColor,
  icon,
  iconBackground,
  tag,
  stats,
  bar,
  onOpen,
}: Props) => (
  <Box $relative $p="16px 20px" $pSM="14px 16px" $minHeight="0">
    <Div $absolute $right="20px" $top="18px" $rightSM="16px" $topSM="16px">
      <Icon $pointer onClick={onOpen}>
        <ArrowRed />
      </Icon>
    </Div>

    <CardRow>
      <Headline>
        <IconTile $background={iconBackground}>{icon}</IconTile>
        <div>
          <TitleLine>
            <H5 $m="0">{title}</H5>
            {tag && (
              <CardTag $color={tag.color} $background={tag.background}>
                {tag.label}
              </CardTag>
            )}
          </TitleLine>
          <H3 $m="0" $color={valueColor} $noWrap>
            {value}
          </H3>
        </div>
      </Headline>

      <InlineStats>
        {stats.map((stat) => (
          <Div
            key={stat.label}
            $minWidth="0"
            data-tooltip-id={TOOLTIP_ID}
            data-tooltip-content={stat.hint}
            data-tooltip-place="top"
          >
            <StatLabel>
              {stat.dot && <StatDot $color={stat.dot} />}
              <Disclaimer $m="0" $color={lightTheme.lightGray} $noWrap>
                {stat.label}
              </Disclaimer>
            </StatLabel>
            <P $m="0" $bold $noWrap>
              {displayCentsInDollars(stat.amount)}
            </P>
          </Div>
        ))}
      </InlineStats>
    </CardRow>

    <Div $mt="14">
      <ValueBar segments={bar.segments} scale={bar.scale} />
    </Div>
  </Box>
);
